import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { FormEvent, useState } from 'react';

type LoginGateProps = {
  onAuthenticated: (userId: string) => void;
};

type OptionsResponse = {
  ok: boolean;
  optionsJSON?: unknown;
  challengeId?: string;
  error?: string;
};

type VerifyResponse = {
  ok: boolean;
  userId?: string;
  error?: string;
};

export function LoginGate({ onAuthenticated }: LoginGateProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bootstrapSecret, setBootstrapSecret] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleLogin() {
    setBusy(true);
    setMessage(null);

    try {
      const options = await postJson<OptionsResponse>('/api/auth/login/options', {});
      if (!options.ok || !options.optionsJSON || !options.challengeId) {
        setMessage(readableError(options.error, 'Passkey login is not ready yet.'));
        return;
      }

      const response = await startAuthentication({ optionsJSON: options.optionsJSON as never });
      const verification = await postJson<VerifyResponse>('/api/auth/login/verify', {
        challengeId: options.challengeId,
        response
      });

      if (verification.ok && verification.userId) {
        onAuthenticated(verification.userId);
      } else {
        setMessage(readableError(verification.error, 'Passkey login failed.'));
      }
    } catch {
      setMessage('Passkey login failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRegister(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);

    try {
      const options = await postJson<OptionsResponse>('/api/auth/register/options', {
        bootstrapSecret,
        email,
        displayName
      });
      if (!options.ok || !options.optionsJSON || !options.challengeId) {
        setMessage(readableError(options.error, 'Registration is not available.'));
        return;
      }

      const response = await startRegistration({ optionsJSON: options.optionsJSON as never });
      const verification = await postJson<VerifyResponse>('/api/auth/register/verify', {
        bootstrapSecret,
        email,
        displayName,
        challengeId: options.challengeId,
        response
      });

      if (verification.ok && verification.userId) {
        onAuthenticated(verification.userId);
      } else {
        setMessage(readableError(verification.error, 'Registration failed.'));
      }
    } catch {
      setMessage('Registration failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel" aria-labelledby="login-heading">
        <div className="login-mark" aria-hidden="true">
          <ShieldCheck size={28} />
        </div>
        <h1 id="login-heading">Passkey required</h1>
        <p>Investment OS is private. Use the owner passkey to continue.</p>

        <div className="login-tabs" aria-label="Authentication mode">
          <button
            type="button"
            aria-pressed={mode === 'login'}
            className={mode === 'login' ? 'active' : ''}
            onClick={() => setMode('login')}
          >
            Sign in
          </button>
          <button
            type="button"
            aria-pressed={mode === 'register'}
            className={mode === 'register' ? 'active' : ''}
            onClick={() => setMode('register')}
          >
            Register
          </button>
        </div>

        {mode === 'login' ? (
          <button type="button" className="primary-action" onClick={handleLogin} disabled={busy}>
            <KeyRound size={18} />
            {busy ? 'Checking passkey' : 'Use passkey'}
          </button>
        ) : (
          <form className="register-form" onSubmit={handleRegister}>
            <label>
              Email
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Display name
              <input
                type="text"
                autoComplete="name"
                required
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </label>
            <label>
              Bootstrap secret
              <input
                type="password"
                autoComplete="one-time-code"
                required
                value={bootstrapSecret}
                onChange={(event) => setBootstrapSecret(event.target.value)}
              />
            </label>
            <button type="submit" className="primary-action" disabled={busy}>
              <KeyRound size={18} />
              {busy ? 'Creating passkey' : 'Create passkey'}
            </button>
          </form>
        )}

        {message ? <p className="login-message">{message}</p> : null}
      </section>
    </main>
  );
}

async function postJson<TBody>(path: string, body: unknown): Promise<TBody> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  return response.json() as Promise<TBody>;
}

function readableError(error: string | undefined, fallback: string): string {
  if (error === 'registration_closed') return 'Registration is already closed.';
  if (error === 'invalid_bootstrap_secret') return 'Bootstrap secret was not accepted.';
  if (error === 'no_credentials') return 'No owner passkey has been registered.';
  return fallback;
}
